import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Users, Heart, BarChart3, Clock } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_COLORS = {
  'Dentist': '#10B981',
  'Patient': '#3B82F6',
  'Friend/Family': '#8B5CF6',
  'Insurance': '#F59E0B',
  'Online/Website': '#EF4444',
  'Other': '#6B7280'
};

const PERIODS = [
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '6m', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: 'all', label: 'All Time' },
];

export default function ReferralAnalyticsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  const loadAnalytics = useCallback(async (p) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/inbound-referrals/analytics?period=${p}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load analytics');
      const data = await res.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadAnalytics(period);
  }, [token, period, loadAnalytics]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
  };

  if (!user) return null;

  const summary = analytics?.summary || {};
  const channels = (analytics?.channelBreakdown || []).filter(c => c.count > 0);
  const topSources = analytics?.topSources || [];
  const trend = analytics?.monthlyTrend || [];
  const recent = analytics?.recentReferrals || [];

  const pieData = channels.map(c => ({
    name: c.type,
    value: c.count,
    fill: CHANNEL_COLORS[c.type] || '#6B7280'
  }));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="referral-analytics-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="analytics-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Referral Source Analytics</h1>
              <p className="text-xs text-gray-500">Patient acquisition by channel</p>
            </div>
          </div>
          <div className="flex gap-1" data-testid="period-selector">
            {PERIODS.map(p => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? 'default' : 'outline'}
                onClick={() => handlePeriodChange(p.value)}
                className="text-xs"
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Referred</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-referred">{summary.totalReferred || 0}</p>
                    </div>
                    <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Sources</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="active-sources">{summary.activeSources || 0}</p>
                      <p className="text-xs text-gray-400">{summary.totalSources || 0} total</p>
                    </div>
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Thank Yous Sent</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-thank-yous">{summary.totalThankYousSent || 0}</p>
                    </div>
                    <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Heart className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Channels</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="channel-count">{channels.length}</p>
                    </div>
                    <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Channel Breakdown - Pie Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-700">Referrals by Channel</CardTitle>
                </CardHeader>
                <CardContent data-testid="channel-pie-chart">
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {pieData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} patients`, 'Referrals']} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
                      No referral data yet. Add referral sources and link patients to see analytics.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Sources - Bar Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-700">Top Referral Sources</CardTitle>
                </CardHeader>
                <CardContent data-testid="top-sources-chart">
                  {topSources.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={topSources.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={120}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [`${value} patients`, props.payload.type]}
                        />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {topSources.slice(0, 8).map((entry, idx) => (
                            <Cell key={idx} fill={CHANNEL_COLORS[entry.type] || '#6B7280'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
                      No referral sources with patients yet.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend */}
            {trend.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-700">Monthly Referral Trend</CardTitle>
                </CardHeader>
                <CardContent data-testid="monthly-trend-chart">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => {
                          const [y, m] = v.split('-');
                          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                          return `${months[parseInt(m)-1]} ${y.slice(2)}`;
                        }}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(v) => {
                          const [y, m] = v.split('-');
                          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                          return `${months[parseInt(m)-1]} ${y}`;
                        }}
                      />
                      <Area type="monotone" dataKey="total" stroke="#10B981" fill="#10B98120" strokeWidth={2} name="Total" />
                      <Area type="monotone" dataKey="Dentist" stroke="#10B981" fill="#10B98110" strokeWidth={1} name="Dentist" />
                      <Area type="monotone" dataKey="Patient" stroke="#3B82F6" fill="#3B82F610" strokeWidth={1} name="Patient" />
                      <Area type="monotone" dataKey="Online/Website" stroke="#EF4444" fill="#EF444410" strokeWidth={1} name="Online/Website" />
                      <Legend />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Bottom Row: Channel List + Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Channel Breakdown Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-700">Channel Summary</CardTitle>
                </CardHeader>
                <CardContent data-testid="channel-table">
                  <div className="space-y-2">
                    {channels.length > 0 ? channels.map(ch => {
                      const pct = summary.totalReferred > 0
                        ? Math.round((ch.count / summary.totalReferred) * 100)
                        : 0;
                      return (
                        <div key={ch.type} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: CHANNEL_COLORS[ch.type] || '#6B7280' }}
                            />
                            <span className="text-sm font-medium text-gray-700">{ch.type}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-900">{ch.count}</span>
                            <Badge variant="outline" className="text-xs w-14 justify-center">
                              {pct}%
                            </Badge>
                          </div>
                        </div>
                      );
                    }) : (
                      <p className="text-sm text-gray-400 text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Referrals */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-700">Recent Referrals</CardTitle>
                </CardHeader>
                <CardContent data-testid="recent-referrals">
                  <div className="space-y-2">
                    {recent.length > 0 ? recent.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{r.patientName}</p>
                          <p className="text-xs text-gray-500">
                            via <span className="font-medium">{r.sourceName}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                              borderColor: CHANNEL_COLORS[r.sourceType] || '#6B7280',
                              color: CHANNEL_COLORS[r.sourceType] || '#6B7280'
                            }}
                          >
                            {r.sourceType}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            {r.date ? new Date(r.date).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    )) : (
                      <p className="text-sm text-gray-400 text-center py-4">No recent referrals</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
